import React, { Suspense, useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import { AuthContext, useAuth } from "./app/AuthContext";
import { authHandlers, loadAuthHttp, loadedAuthHttp } from "./app/authHttpLoader";

import { VynkoBrandProvider } from "./nfc/VynkoBrandContext";
import { isVynkoHost } from "./nfc/vynkoBrand";

import { API_BASE } from "./lib/apiBase";
import { lazyRetry, lazyWithI18n } from "./lib/lazyRetry";
import RouteFallback from "./app/RouteFallback";
import PublicErrorBoundary from "./components/PublicErrorBoundary";
import HtmlLangSync from "./components/HtmlLangSync";
import { isPublicFirstPaintPath } from "./lib/publicPaths";
import webStudioTerms from "./lib/webStudioTerms.json";

const LoginPage = lazyWithI18n(() => import("./pages/LoginPage"));
const BootstrapPage = lazyWithI18n(() => import("./pages/BootstrapPage"));
const ForgotPasswordPage = lazyWithI18n(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyWithI18n(() => import("./pages/ResetPasswordPage"));
const ServicesIndexPage = lazyRetry(() => import("./pages/ServicesIndexPage"));
const ServicesIndexPageEs = lazyRetry(() => import("./pages/ServicesIndexPageEs"));
const GetHelpPage = lazyRetry(() => import("./pages/GetHelpPage"));
const GetHelpPageEs = lazyRetry(() => import("./pages/GetHelpPageEs"));
const WorkflowAuditLandingPage = lazyRetry(() => import("./pages/WorkflowAuditLandingPage"));
const RecommendedToolsPage = lazyRetry(() => import("./pages/RecommendedToolsPage"));
const AIReadinessScorecardPage = lazyRetry(() => import("./pages/AIReadinessScorecardPage"));
const InstantReportPage = lazyRetry(() => import("./pages/InstantReportPage"));
const InstantReportSamplePage = lazyRetry(() => import("./pages/InstantReportSamplePage"));
const InstantReportPageEs = lazyRetry(() => import("./pages/InstantReportPageEs"));
const InstantReportSamplePageEs = lazyRetry(() => import("./pages/InstantReportSamplePageEs"));
const WebsiteSecurityCheckPage = lazyRetry(() => import("./pages/WebsiteSecurityCheckPage"));
const WebLaunchAuditPage = lazyRetry(() => import("./pages/WebLaunchAuditPage"));
const UnsubscribePage = lazyRetry(() => import("./pages/UnsubscribePage"));
const PrivacyPolicyPage = lazyRetry(() => import("./pages/PrivacyPolicyPage"));
const CookiePolicyPage = lazyRetry(() => import("./pages/CookiePolicyPage"));
const TermsOfServicePage = lazyRetry(() => import("./pages/TermsOfServicePage"));
const LandingPagesAgentsPage = lazyRetry(() => import("./pages/LandingPagesAgentsPage"));
const WorkflowAuditLandingPageEs = lazyRetry(() => import("./pages/WorkflowAuditLandingPageEs"));
const AIReadinessScorecardPageEs = lazyRetry(() => import("./pages/AIReadinessScorecardPageEs"));
const WebsiteSecurityCheckPageEs = lazyRetry(() => import("./pages/WebsiteSecurityCheckPageEs"));
const WebLaunchAuditPageEs = lazyRetry(() => import("./pages/WebLaunchAuditPageEs"));
const LandingPagesAgentsPageEs = lazyRetry(() => import("./pages/LandingPagesAgentsPageEs"));
const PrivacyPolicyPageEs = lazyRetry(() => import("./pages/PrivacyPolicyPageEs"));
const CookiePolicyPageEs = lazyRetry(() => import("./pages/CookiePolicyPageEs"));
const TermsOfServicePageEs = lazyRetry(() => import("./pages/TermsOfServicePageEs"));
const WebStudioLegalPage = lazyRetry(() => import("./legal/webStudio/WebStudioLegalPage"));
// Search-intent landing pages (growth SEO pass 2026-10-29, pages/intent/).
const AutomateAdminPage = lazyRetry(() => import("./pages/AutomateAdminPage"));
const AutomateAdminPageEs = lazyRetry(() => import("./pages/AutomateAdminPageEs"));
const EnquiriesQuotesPage = lazyRetry(() => import("./pages/EnquiriesQuotesPage"));
const EnquiriesQuotesPageEs = lazyRetry(() => import("./pages/EnquiriesQuotesPageEs"));
const WebsiteAssistantPage = lazyRetry(() => import("./pages/WebsiteAssistantPage"));
const WebsiteAssistantPageEs = lazyRetry(() => import("./pages/WebsiteAssistantPageEs"));
const LandingPageGuidePage = lazyRetry(() => import("./pages/LandingPageGuidePage"));
const LandingPageGuidePageEs = lazyRetry(() => import("./pages/LandingPageGuidePageEs"));
const AIForAccountantsPage = lazyRetry(() => import("./pages/AIForAccountantsPage"));
const AIForAccountantsPageEs = lazyRetry(() => import("./pages/AIForAccountantsPageEs"));
const AIForPropertyManagersPage = lazyRetry(() => import("./pages/AIForPropertyManagersPage"));
const AIForPropertyManagersPageEs = lazyRetry(() => import("./pages/AIForPropertyManagersPageEs"));
const AIForLogisticsFirmsPage = lazyRetry(() => import("./pages/AIForLogisticsFirmsPage"));
const AIForLogisticsFirmsPageEs = lazyRetry(() => import("./pages/AIForLogisticsFirmsPageEs"));
const AIForRecruitmentAgenciesPage = lazyRetry(() => import("./pages/AIForRecruitmentAgenciesPage"));
const AIForRecruitmentAgenciesPageEs = lazyRetry(() => import("./pages/AIForRecruitmentAgenciesPageEs"));
const NFCRedirectPage = lazyWithI18n(() => import("./pages/NFCRedirectPage"));
const NFCLoyaltyPage = lazyWithI18n(() => import("./pages/NFCLoyaltyPage"));
const NFCLoyaltyStampPage = lazyWithI18n(() => import("./pages/NFCLoyaltyStampPage"));
const NFCOfferPage = lazyWithI18n(() => import("./pages/NFCOfferPage"));
const NFCCardLandingPage = lazyWithI18n(() => import("./pages/NFCCardLandingPage"));
const NFCCustomerDashboardPage = lazyWithI18n(() => import("./pages/NFCCustomerDashboardPage"));
const NFCStorePage = lazyWithI18n(() => import("./pages/NFCStorePage"));
const NFCLandingPage = lazyWithI18n(() => import("./pages/NFCLandingPage"));
const NFCCatalogPage = lazyWithI18n(() => import("./pages/NFCCatalogPage"));
const NFCPayPage = lazyWithI18n(() => import("./pages/NFCPayPage"));
const NFCOrderConfirmationPage = lazyWithI18n(() => import("./pages/NFCOrderConfirmationPage"));
const VynkoCollectionPage = lazyWithI18n(() => import("./pages/vynko/VynkoCollectionPage"));
const VynkoProductPage = lazyWithI18n(() => import("./pages/vynko/VynkoProductPage"));
const VynkoRoutes = lazyWithI18n(() => import("./routes/VynkoRoutes"));
const WebStudioRoutes = lazyWithI18n(() => import("./routes/WebStudioRoutes"));
// lazyWithI18n: pages that translate (sign-in, NFC, Vynko, Web Studio and the owner Panel, its own chunk with the
// header, tabs and every signed-in page) load i18n with them; the public pages above never load either.
const AppShell = lazyWithI18n(() => import("./app/PanelShell"));
const WEB_STUDIO_ENABLED = process.env.REACT_APP_WEB_STUDIO === "1";

export { AuthContext, useAuth };

function decodeJwt(token) {
  try {
    const payload = token.split(".")[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

function buildUserFromToken(token) {
  const payload = decodeJwt(token);
  if (!payload) return null;
  return {
    id: payload.sub || "",
    email: payload.email || "",
    role: payload.role || "user",
  };
}



function AppRoutes() {
  const location = useLocation();
  const { token, bootstrapNeeded, bootstrapLoading } = useAuth();

  // Fase 2 of the Vynko launch plan -- host-only decision (resolveBrand's
  // second argument is fixed to "/", ignoring the product-route path
  // override) about which COMPLETE, SELF-CONTAINED route table to mount.
  // Short-circuits before any bootstrap/auth logic runs at all: a Vynko
  // visitor must never see the Director's "Checking setup..." loading
  // state, never get redirected to /bootstrap, never touch AppShell. This
  // is deliberately NOT a DirectorRoutes.js extraction -- everything below
  // this block is the pre-existing Director route tree, unchanged, so
  // revosdirector.com's behavior carries zero risk from this change. The
  // branch here is what makes the pattern reusable for a future venture
  // #3: one more `else if` on brandId, not a rewrite.
  //
  // Uses isVynkoHost() (vynkoBrand.js's purpose-built host-only check), not
  // resolveBrand(hostname, "/") -- found by adversarial review: calling
  // resolveBrand with a hardcoded "/" only worked because no
  // PRODUCT_ROUTE_PREFIXES entry happens to match the literal string "/";
  // isVynkoHost() is correct by construction instead of by that
  // coincidence, and is the exact function vynkoBrand.js exports for this.
  const isVynko = isVynkoHost(typeof window !== "undefined" ? window.location.hostname : "");
  if (isVynko) {
    return (
      <PublicErrorBoundary key={location.pathname}>
        <Suspense fallback={<RouteFallback />}>
          <VynkoRoutes />
        </Suspense>
      </PublicErrorBoundary>
    );
  }

  // Public pages paint at once (and an anonymous "/" goes straight to /services); only the Panel waits
  // for the bootstrap check (lib/publicPaths.js).
  const paintsAtOnce = isPublicFirstPaintPath(location.pathname) || (!token && location.pathname === "/");
  if (bootstrapLoading && !paintsAtOnce) {
    return <div style={{ padding: 24 }}>Checking setup…</div>;
  }

  if (bootstrapNeeded && location.pathname !== "/bootstrap") {
    return <Navigate to="/bootstrap" replace />;
  }

  if (!bootstrapNeeded && location.pathname === "/bootstrap") {
    return <Navigate to={token ? "/" : "/login"} replace />;
  }

  return <DirectorRouteTable token={token} />;
}

// The Director route table. Memoised on `token` (it reads the location itself), so the bootstrap check
// answering while a public page is already on screen (see lib/publicPaths.js) does not re-render that page.
const DirectorRouteTable = React.memo(function DirectorRouteTable({ token }) {
  const location = useLocation();
  return (
    <PublicErrorBoundary key={location.pathname}>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {WEB_STUDIO_ENABLED && <Route path="/web/*" element={<WebStudioRoutes />} />}
      <Route path="/login" element={token ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/bootstrap" element={<BootstrapPage />} />
      <Route path="/forgot-password" element={token ? <Navigate to="/" replace /> : <ForgotPasswordPage />} />
      {/* Not redirected when signed in: a signed-in owner who follows an emailed reset link must be able to set the new password (the link carries its own one-time token). */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/services" element={<ServicesIndexPage />} />
      <Route path="/es/servicios" element={<ServicesIndexPageEs />} />
      <Route path="/get-help" element={<GetHelpPage />} />
      <Route path="/es/pedir-ayuda" element={<GetHelpPageEs />} />
      <Route path="/workflow-audit" element={<WorkflowAuditLandingPage />} />
      <Route path="/workflow-audit/tools" element={<RecommendedToolsPage lang="en" />} />
      <Route path="/ai-readiness-scorecard" element={<AIReadinessScorecardPage />} />
      <Route path="/instant-report" element={<InstantReportPage />} />
      <Route path="/instant-report/sample" element={<InstantReportSamplePage />} />
      <Route path="/es/informe-instantaneo" element={<InstantReportPageEs />} />
      <Route path="/es/informe-instantaneo/ejemplo" element={<InstantReportSamplePageEs />} />
      <Route path="/website-security-check" element={<WebsiteSecurityCheckPage />} />
      <Route path="/website-launch-check" element={<WebLaunchAuditPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/cookie-policy" element={<CookiePolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/landing-pages-and-agents" element={<LandingPagesAgentsPage />} />
      {/* Intent landing pages (2026-10-29 growth plan). Paths are mirrored in director_shell_service.py,
          public/sitemap.xml and lib/publicPaths.js. */}
      <Route path="/automate-small-business-admin" element={<AutomateAdminPage />} />
      <Route path="/es/automatizar-tareas-administrativas" element={<AutomateAdminPageEs />} />
      <Route path="/automate-enquiries-and-quotes" element={<EnquiriesQuotesPage />} />
      <Route path="/es/automatizar-consultas-y-presupuestos" element={<EnquiriesQuotesPageEs />} />
      <Route path="/ai-assistant-for-customer-enquiries" element={<WebsiteAssistantPage />} />
      <Route path="/es/asistente-ia-para-consultas-de-clientes" element={<WebsiteAssistantPageEs />} />
      <Route path="/landing-page-for-small-business" element={<LandingPageGuidePage />} />
      <Route path="/es/landing-page-para-pequenas-empresas" element={<LandingPageGuidePageEs />} />
      {/* Sector landing pages (2026-09-24 traffic plan, section 5). Paths are mirrored in
          director_shell_service.py, server.py, public/sitemap.xml and ops/Caddyfile. */}
      <Route path="/ai-for-accountants" element={<AIForAccountantsPage />} />
      <Route path="/es/ia-para-asesorias" element={<AIForAccountantsPageEs />} />
      <Route path="/ai-for-property-managers" element={<AIForPropertyManagersPage />} />
      <Route path="/es/ia-para-gestion-inmobiliaria" element={<AIForPropertyManagersPageEs />} />
      <Route path="/ai-for-logistics-firms" element={<AIForLogisticsFirmsPage />} />
      <Route path="/es/ia-para-empresas-de-logistica" element={<AIForLogisticsFirmsPageEs />} />
      <Route path="/ai-for-recruitment-agencies" element={<AIForRecruitmentAgenciesPage />} />
      <Route path="/es/ia-para-agencias-de-seleccion" element={<AIForRecruitmentAgenciesPageEs />} />
      <Route path="/es/auditoria-ia" element={<WorkflowAuditLandingPageEs />} />
      <Route path="/es/auditoria-ia/recursos" element={<RecommendedToolsPage lang="es" />} />
      <Route path="/es/evaluacion-preparacion-ia" element={<AIReadinessScorecardPageEs />} />
      <Route path="/es/landing-pages-agentes-ia" element={<LandingPagesAgentsPageEs />} />
      <Route path="/es/verificacion-seguridad-web" element={<WebsiteSecurityCheckPageEs />} />
      <Route path="/es/verificacion-lanzamiento-web" element={<WebLaunchAuditPageEs />} />
      <Route path="/es/politica-privacidad" element={<PrivacyPolicyPageEs />} />
      <Route path="/es/politica-cookies" element={<CookiePolicyPageEs />} />
      <Route path="/es/terminos-de-servicio" element={<TermsOfServicePageEs />} />
      {/* Web Studio legal documents (EN/ES), public whether or not Web Studio is switched on: paths and version
          come from lib/webStudioTerms.json, the twin of backend/data/legal/web_studio_terms.json. */}
      {Object.entries(webStudioTerms.documents).flatMap(([doc, paths]) =>
        ["en", "es"].map((lang) => (
          <Route key={paths[lang]} path={paths[lang]} element={<WebStudioLegalPage doc={doc} lang={lang} />} />
        ))
      )}
      {/* NFC product line -- public, unauthenticated (customer auth is a
          separate system, see NFCCustomerAuthContext.js). /c/:slug is the
          SPA-fallback resolution path (see NFCRedirectPage.js's own
          comment) until an ops-level Caddy rule routes /c/* straight to
          the backend. */}
      <Route path="/c/:slug" element={<NFCRedirectPage />} />
      <Route path="/p/:slug" element={<NFCCardLandingPage />} />
      <Route path="/my-cards" element={<NFCCustomerDashboardPage />} />
      {/* /tap and /es/tarjetas-nfc are the marketing front doors (L2);
          the order form lives one level down at .../order|/pedido so the
          memorable brand URL always resolves to "what is this", not
          straight to a checkout form. See NFCLandingPage.js's own
          comment for the reasoning. */}
      <Route path="/tap" element={<NFCLandingPage lang="en" />} />
      <Route path="/tap/catalog" element={<NFCCatalogPage lang="en" />} />
      <Route path="/tap/order" element={<NFCStorePage lang="en" />} />
      {/* NFCProductGallery's "View details" tile link and the collection
          facet chips both point at these paths regardless of which route
          table rendered the gallery (App.js's own NFCCatalogPage mount
          above, or VynkoRoutes.js's) -- mounted here too so a click on
          revosdirector.com/tap/catalog doesn't fall through to the
          Director's own catch-all/login redirect (found by adversarial
          review). Both pages resolve their own brand via useBrand(),
          which already defaults correctly off getvynko.com. */}
      <Route path="/products/:sku" element={<VynkoProductPage />} />
      <Route path="/for/:slug" element={<VynkoCollectionPage kind="audience" />} />
      <Route path="/use/:slug" element={<VynkoCollectionPage kind="use_case" />} />
      <Route path="/es/products/:sku" element={<VynkoProductPage lang="es" />} />
      <Route path="/es/for/:slug" element={<VynkoCollectionPage kind="audience" lang="es" />} />
      <Route path="/es/use/:slug" element={<VynkoCollectionPage kind="use_case" lang="es" />} />
      <Route path="/es/tarjetas-nfc" element={<NFCLandingPage lang="es" />} />
      <Route path="/es/tarjetas-nfc/catalogo" element={<NFCCatalogPage lang="es" />} />
      <Route path="/es/tarjetas-nfc/pedido" element={<NFCStorePage lang="es" />} />
      <Route path="/pay/:tableSlug" element={<NFCPayPage />} />
      <Route path="/loyalty/:slug" element={<NFCLoyaltyPage />} />
      <Route path="/loyalty/:slug/stamp" element={<NFCLoyaltyStampPage />} />
      <Route path="/offer/:slug" element={<NFCOfferPage />} />
      {/* Stripe's success_url for an NFC order (see
          nfc_order_service.py::create_order()) -- must never fall through
          to the Director's own "/" -> ProtectedRoute -> /login redirect, or
          a customer who just paid lands on a login screen for a different
          product entirely. */}
      <Route path="/nfc-order-confirmation" element={<NFCOrderConfirmationPage />} />
      {/* Bare "/" for an unauthenticated visitor used to fall straight
          through to the "/*" AppShell catch-all below -> ProtectedRoute ->
          /login: anyone who typed revosdirector.com directly (e.g. a cold-
          outreach recipient checking out the sender before replying)
          landed on the internal owner login form -- no marketing content,
          no explanation of what the business is -- instead of a public
          page. ServicesIndexPage.js's own comment already named this gap
          when /services was built ("revosdirector.com/ redirects to
          /login") but never corrected the "/" redirect itself. This route
          closes it the same way the NFC order-confirmation route above
          does for a different product line: send an unauthenticated
          visitor to the real public front door instead. Logged-in owner
          traffic is unaffected -- with a token this still falls through to
          AppShell's own "/" -> TodayPage, unchanged. */}
      <Route path="/" element={token ? <AppShell /> : <Navigate to="/services" replace />} />
      <Route path="/*" element={<AppShell />} />
    </Routes>
    </Suspense>
    </PublicErrorBoundary>
  );
});

export default function App() {
  const [token, setToken] = useState(localStorage.getItem("auth_token"));
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("auth_token");
    return stored ? buildUserFromToken(stored) : null;
  });
  const [loading, setLoading] = useState(true);
  const [bootstrapNeeded, setBootstrapNeeded] = useState(false);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);

  // axios and its auth handling (bearer header, refresh on 401) live in app/authHttp.js, loaded on demand so the
  // public pages never download axios; a refresh reports back through these handlers.
  useEffect(() => {
    authHandlers.onRefreshed = (newToken) => setToken(newToken);
    authHandlers.onSignedOut = () => {
      setToken(null);
      setUser(null);
    };
  }, []);

  useEffect(() => {
    if (token) {
      loadAuthHttp()
        .then((http) => http.setAuthHeader(token))
        .catch((error) => console.error("Auth client failed to load:", error));
    } else {
      loadedAuthHttp()?.setAuthHeader(null);
    }
  }, [token]);

  const checkBootstrap = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/check-bootstrap`);
      const data = await response.json();
      setBootstrapNeeded(Boolean(data.bootstrap_needed));
    } catch (error) {
      console.error("Bootstrap check failed:", error);
      setBootstrapNeeded(false);
    } finally {
      setBootstrapLoading(false);
    }
  };

  const refreshMe = async (currentToken = token) => {
    if (!currentToken) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      // Routed through axios (not fetch) so an expired access token on cold
      // load goes through the same 401 -> refresh-token -> retry interceptor
      // as every other API call, instead of forcing a logout the moment the
      // stored access token happens to already be past its 30-minute expiry.
      const http = await loadAuthHttp();
      const { data } = await http.axios.get(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      setUser(data.user || buildUserFromToken(currentToken));
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("refresh_token");
        setToken(null);
        setUser(null);
      } else {
        console.error("Auth refresh failed:", error);
        setUser(buildUserFromToken(currentToken));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Vynko visitors never see the Director's bootstrap wizard (AppRoutes
    // already short-circuits to VynkoRoutes before this loading state could
    // matter), so there's no reason to make this authenticated-looking
    // check-bootstrap call on their behalf at all -- one fewer unnecessary
    // request against api.getvynko.com on every Vynko page load.
    const hostname = typeof window !== "undefined" ? window.location.hostname : "";
    if (isVynkoHost(hostname)) {
      setBootstrapLoading(false);
      return;
    }
    checkBootstrap();
  }, []);

  useEffect(() => {
    refreshMe(token);
  }, [token]);

  const login = async (email, password) => {
    const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        typeof data?.detail === "string"
          ? data.detail
          : data?.message || "Login failed"
      );
    }

    localStorage.setItem("auth_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setToken(data.access_token);

    const safeUser = data.user || buildUserFromToken(data.access_token);
    setUser(safeUser);

    return data;
  };

  const bootstrap = async (email, password) => {
    const url = `${API_BASE}/api/auth/bootstrap?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;

    const response = await fetch(url, { method: "POST" });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        typeof data?.detail === "string"
          ? data.detail
          : data?.message || "Bootstrap failed"
      );
    }

    localStorage.setItem("auth_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setToken(data.access_token);

    const safeUser = data.user || buildUserFromToken(data.access_token);
    setUser(safeUser);

    setBootstrapNeeded(false);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    loadedAuthHttp()?.setAuthHeader(null);
    setToken(null);
    setUser(null);
  };

  const value = useMemo(
    () => ({
      apiBase: API_BASE,
      token,
      user,
      loading,
      bootstrapNeeded,
      bootstrapLoading,
      login,
      bootstrap,
      logout,
      refreshMe,
      setUser,
    }),
    [token, user, loading, bootstrapNeeded, bootstrapLoading]
  );

  return (
    <HelmetProvider>
      <AuthContext.Provider value={value}>
        <Router>
          <VynkoBrandProvider>
            <HtmlLangSync />
            <AppRoutes />
          </VynkoBrandProvider>
        </Router>
      </AuthContext.Provider>
    </HelmetProvider>
  );
}
